import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';

/** Material UI Packages. */
import { Box, Paper, Typography } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { GridToolbar } from '@mui/x-data-grid-premium';

/** Redux Packages. */
import { useDispatch, useSelector } from 'react-redux';

/** Redux Reducers. */
import { setAddOnsData, addAddOnsData, updateAddOnsData, deleteAddOnsData } from '../../state/slices/addOnsSlice';
import { setSnackbar } from '../../state/slices/snackBarSlice';

/** Auth0 authenticator Packages. */
import { useAuth0 } from '@auth0/auth0-react';

/** Internal Components. */
import { callAPI } from '../../api/useApiRequest';
import CustomSnackbar from '../../components/CustomSnackbar';
import { CustomSkeleton2 } from '../../components/Skeleton';
import FormField from '../../components/FormField';
import ThemeButton from '../../components/Button';
import CircularLoading from '../../components/CircularLoading';

import { addOnsCol } from './TableDef';

const ADDON_MASTER_ERRORS = {
    HAS_MAPPINGS_MESSAGE: 'Addon is still mapped to account(s) and cannot be deleted',
    HAS_MAPPINGS_CODE: 'ADDON_MAPPED',
    HAS_MAPPINGS_SNACKBAR: 'This add-on is still mapped to account(s). Remove mappings first.',
};

/** Snackbar messages. */
const SNACKBAR_MESSAGES = {
    ADD_SUCCESS: 'Add-ons have been successfully created.',
    DELETE_SUCCESS: 'Add-ons have been successfully deleted.',
    UPDATE_SUCCESS: 'Add-ons have been successfully updated.',
    ADDON_NAME_REQUIRED: 'Add-on name is required.',
    ADDON_ALREADY_EXISTS: 'This Add-on is already added.',
    ADDON_DELETE_MAPPED: ADDON_MASTER_ERRORS.HAS_MAPPINGS_SNACKBAR,
    ERROR_MESSAGE: 'Something went wrong, please try again.',
};

export default function Addons() {
    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();
    /** Get authentication details from Auth0. */
    const { user, getAccessTokenSilently } = useAuth0();

    /** Get the add-ons list from the store. */
    const addOnsState = useSelector((state) => state.addOns);
    const addOnsList = Array.isArray(addOnsState) ? addOnsState : [];

    /** State for the add-on name. */
    const [addOnName, setAddOnName] = useState('');
    const [loading, setLoading] = useState({ get: false, post: false, delete: false });

    /** Show snackbar message. */
    const showSnackbar = useCallback(
        (message, severity = 'info') => {
            dispatch(setSnackbar({ open: true, message, severity }));
        },
        [dispatch],
    );

    const getApiErrorMessage = (error) =>
        error?.response?.data?.message || error?.response?.data?.error || error?.message || '';

    const getApiErrorCode = (errorMessage) => {
        const codeMatch = errorMessage.match(/CODE:\s*([A-Z0-9_]+)/);
        return codeMatch ? codeMatch[1] : null;
    };

    /** Validate the add-on name. */
    const validateAddOnName = useCallback(
        (name) => {
            if (!name.trim()) {
                showSnackbar(SNACKBAR_MESSAGES.ADDON_NAME_REQUIRED, 'warning');
                return false;
            }

            const isDuplicate = addOnsList.some((row) => row.name.toLowerCase() === name.trim().toLowerCase());

            if (isDuplicate) {
                showSnackbar(SNACKBAR_MESSAGES.ADDON_ALREADY_EXISTS, 'warning');
                return false;
            }

            return true;
        },
        [addOnsList, showSnackbar],
    );

    /** Handle API success response */
    const handleApiSuccess = useCallback(
        (method, newRow) => {
            let message = '';

            switch (method) {
                case 'post':
                    dispatch(addAddOnsData(newRow));
                    message = SNACKBAR_MESSAGES.ADD_SUCCESS;
                    break;
                case 'delete':
                    dispatch(deleteAddOnsData(newRow));
                    message = SNACKBAR_MESSAGES.DELETE_SUCCESS;
                    break;
                case 'put':
                    dispatch(updateAddOnsData(newRow));
                    message = SNACKBAR_MESSAGES.UPDATE_SUCCESS;
                    break;
                default:
                    return;
            }

            showSnackbar(message, 'success');
        },
        [showSnackbar],
    );

    /** Fetch the add-ons from the API. (API: POST, PUT, DELETE ADDONS) */
    const fetchAddOns = useCallback(
        (newRow, method) => {
            callAPI({
                method,
                module: 'addons',
                component: null,
                getAccessTokenSilently,
                email: user.email,
                obj: JSON.stringify(newRow),
                onInit: () => {
                    setLoading((prev) => ({ ...prev, post: true }));
                },
                onSuccess: (resp) => {
                    handleApiSuccess(method, resp.data);
                },
                onError: (error) => {
                    const errorMessage = getApiErrorMessage(error);
                    const errorCode = getApiErrorCode(errorMessage);
                    const isMappedDeleteError =
                        method === 'delete' &&
                        (errorCode === ADDON_MASTER_ERRORS.HAS_MAPPINGS_CODE ||
                            errorMessage.includes(ADDON_MASTER_ERRORS.HAS_MAPPINGS_MESSAGE) ||
                            errorMessage.includes('foreign key constraint fails'));

                    if (isMappedDeleteError) {
                        showSnackbar(SNACKBAR_MESSAGES.ADDON_DELETE_MAPPED, 'warning');
                        return;
                    }

                    showSnackbar(SNACKBAR_MESSAGES.ERROR_MESSAGE, 'error');
                },
                onFinal: () => {
                    setLoading((prev) => ({ ...prev, post: false }));
                },
            });
        },
        [getAccessTokenSilently, user.email, showSnackbar],
    );

    /** Handle to change the name of an add-on. */
    const handleChangeAddOnName = (e) => {
        setAddOnName(e.target.value);
    };

    /** Handle to add an add-on. */
    const handleAddAddOn = () => {
        if (!validateAddOnName(addOnName)) return;

        const maxId = addOnsList.length > 0 ? Math.max(...addOnsList.map((row) => row.id)) : 0;

        const newAddOn = {
            id: maxId + 1,
            name: addOnName.toUpperCase(),
            status: true,
        };

        fetchAddOns(newAddOn, 'post');
        setAddOnName('');
    };

    /** Handle to delete an add-on. */
    const handleDeleteAddOn = (event, id) => {
        event.stopPropagation();
        if (window.confirm('Are you sure you want to delete this add-on?')) {
            fetchAddOns({ id }, 'delete');
        }
    };

    /** Handle to process a row update. */
    const processRowUpdate = (newRow) => {
        fetchAddOns(newRow, 'put');
        return newRow;
    };

    /** Handle to toggle the status of an add-on. */
    const handleStatusToggle = (id) => {
        fetchAddOns(
            {
                ...addOnsList.find((row) => row.id === id),
                status: !addOnsList.find((row) => row.id === id).status,
            },
            'put',
        );
    };

    /** Fetch the initial data from the API. (API: GET ADDONS) */
    const fetchInitialData = useCallback(() => {
        callAPI({
            method: 'get',
            module: 'addons',
            component: null,
            getAccessTokenSilently,
            email: user.email,
            onInit: () => {
                setLoading((prev) => ({ ...prev, get: true }));
            },
            onSuccess: (resp) => {
                dispatch(setAddOnsData(resp.data));
            },
            onError: () => showSnackbar(SNACKBAR_MESSAGES.ERROR_MESSAGE, 'error'),
            onFinal: () => {
                setLoading((prev) => ({ ...prev, get: false }));
            },
        });
    }, [getAccessTokenSilently, user.email, showSnackbar]);

    /** Set the title of the page. */
    useEffect(() => {
        document.title = 'Addons | Management';
        fetchInitialData();
    }, []);

    if (loading.post || loading.delete) {
        return <CircularLoading loading={loading.post || loading.delete} />;
    }

    return (
        <Box sx={{ maxWidth: '97%', mt: 2, mx: 'auto', height: '90vh' }}>
            <Paper
                sx={{
                    borderRadius: 5,
                    p: 1,
                    height: '100%',
                }}
            >
                {loading.get ? (
                    <CustomSkeleton2 />
                ) : (
                    <>
                        {/** Add-on name input field. */}
                        <Box sx={{ my: 1, p: 1, display: 'flex', alignItems: 'center', gap: 2, height: '5%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                    sx={{
                                        width: '3px',
                                        height: '16px',
                                        backgroundColor: 'primary.main',
                                    }}
                                />
                                <Typography sx={{ fontWeight: 500, fontSize: 13, color: 'text.secondary' }}>
                                    Add-on Name
                                </Typography>
                            </Box>
                            <FormField
                                label=""
                                value={addOnName}
                                onChange={handleChangeAddOnName}
                                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                labelStyle={{ width: '0px', gap: '0px' }}
                                textFieldStyle={{
                                    width: '300px',
                                    '& .MuiInputBase-input': {
                                        py: 0.6,
                                        fontSize: 13,
                                    },
                                }}
                            />
                            <ThemeButton text="Add" onClick={handleAddAddOn} sx={{ alignSelf: 'flex-end' }} />
                            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                                Inactive Add-ons are centrally controlled, preserve existing mappings as OFF, and block
                                new mappings.
                            </Typography>
                        </Box>

                        {/** Data grid for the add-ons. */}
                        <DataGridPremium
                            rows={addOnsList}
                            columns={addOnsCol(handleStatusToggle, handleDeleteAddOn)}
                            density="compact"
                            processRowUpdate={processRowUpdate}
                            disableRowSelectionOnClick
                            unstable_cellSelection
                            disableDensitySelector
                            disablePrintSelection
                            slots={{ toolbar: GridToolbar }}
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                },
                            }}
                            sx={{ border: 'none', height: '93%' }}
                        />
                        <CustomSnackbar />
                    </>
                )}
            </Paper>
        </Box>
    );
}
